import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  PublicClientApplication, 
  type AccountInfo,
  InteractionRequiredAuthError
} from '@azure/msal-browser'
import { msalConfig, loginRequest, tokenRequest } from '@/config/msal'
import router from '@/router'

export interface User {
  id: string
  name?: string
  username?: string
  email?: string
}

export const useAuthStore = defineStore('auth', () => {
  const msalInstance = new PublicClientApplication(msalConfig)
  const account = ref<AccountInfo | null>(null)
  const accessToken = ref<string | null>(null)
  const isInitialized = ref(false)

  const isAuthenticated = computed(() => !!account.value && !!accessToken.value)
  
  // Expose account for role checking
  const getAccount = computed(() => account.value)
  
  const user = computed((): User | null => {
    if (!account.value) return null
    
    return {
      id: account.value.localAccountId,
      name: account.value.name || account.value.username,
      username: account.value.username,
      email: account.value.username
    }
  })

  const clearAuthState = () => {
    account.value = null
    accessToken.value = null
  }

  const clearAuthStateAndRedirectToLogin = () => {
    clearAuthState()
    router.push('/login')
  }

  /**
   * Helper to execute MSAL popup with centered positioning
   * Temporarily overrides window.open to force popup centering
   */
  const withCenteredPopup = async <T>(popupFn: () => Promise<T>): Promise<T> => {
    const originalWindowOpen = window.open
    
    try {
      window.open = function(url?: string | URL, target?: string) {
        const popupWidth = 483
        const popupHeight = 600
        const screenLeft = window.screenLeft ?? window.screenX
        const screenTop = window.screenTop ?? window.screenY
        const screenWidth = window.screen.width
        const screenHeight = window.screen.height
        
        const left = Math.round(screenLeft + (screenWidth - popupWidth) / 2)
        const top = Math.round(screenTop + (screenHeight - popupHeight) / 2)
        
        const centeredFeatures = `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
        
        return originalWindowOpen.call(window, url, target, centeredFeatures)
      } as typeof window.open
      
      return await popupFn()
    } finally {
      window.open = originalWindowOpen
    }
  }

  let initializePromise: Promise<void> | null = null

  // Runs once per page load; concurrent callers share the same in-flight initialization
  const initialize = () => {
    initializePromise ??= initializeMsal()
    return initializePromise
  }

  const initializeMsal = async () => {
    try {
      await msalInstance.initialize()
      
      const response = await msalInstance.handleRedirectPromise()
      if (response) {
        account.value = response.account
        accessToken.value = response.accessToken
      } else {
        // Prefer the account signed in last (active account); the cache can hold stale accounts with expired tokens
        const cachedAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
        if (cachedAccount) {
          account.value = cachedAccount
          msalInstance.setActiveAccount(cachedAccount)

          try {
            const tokenResponse = await msalInstance.acquireTokenSilent({ ...tokenRequest, account: cachedAccount })
            accessToken.value = tokenResponse.accessToken
          } catch (error) {
            console.warn('Silent token acquisition failed:', error)
            throw error
          }
        }
      }
    } catch (error) {
      clearAuthState();
      console.error('MSAL initialization failed:', error)
    } finally {
      isInitialized.value = true
    }
  }

  const login = async () => {
    try {
      const loginResponse = await withCenteredPopup(() => msalInstance.loginPopup(loginRequest))

      if (!loginResponse.account) {
        throw new Error('Login succeeded but no account returned')
      }

      const token = await getAccessTokenOrThrow(loginResponse.account)

      account.value = loginResponse.account
      accessToken.value = token
      msalInstance.setActiveAccount(loginResponse.account)

    } catch (error) {
      clearAuthState()
      console.error('Login failed:', error)
      throw error
    }
  }

  const logout = async () => {
    try {
      await withCenteredPopup(() => msalInstance.logoutPopup({ account: account.value || undefined }))
    } catch (error) {
      console.error('Logout failed:', error)
      throw error
    } finally {
      clearAuthStateAndRedirectToLogin()
    }
  }

  const getAccessTokenOrThrow = async (acc: AccountInfo = account.value!): Promise<string | null> => {
    if (!acc) return null

    try {
      const response = await msalInstance.acquireTokenSilent({ ...tokenRequest, account: acc })
      accessToken.value = response.accessToken
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await withCenteredPopup(() => msalInstance.acquireTokenPopup(tokenRequest))
          accessToken.value = response.accessToken
          return response.accessToken
        } catch (popupError) {
          console.error('Token acquisition via popup failed:', popupError)
          throw error
        }
      } else {
        console.error('Token acquisition failed:', error)
        throw error
      }
    }
  }


  const getAccessToken = async (_account: AccountInfo = account.value!): Promise<string | null> => {
    try {
      return await getAccessTokenOrThrow(_account)
    } catch {
      return null
    }
  }

  const refreshToken = async (): Promise<string | null> => {
    if (!account.value) {
      console.warn('No account available for token refresh')
      return null
    }

    try {
      
      const response = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: account.value,
        forceRefresh: true
      })
      
      accessToken.value = response.accessToken

      return response.accessToken
      
    } catch (error) {
      console.warn('Silent token refresh failed:', error)
      
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await withCenteredPopup(() => msalInstance.acquireTokenPopup({ ...tokenRequest }))
          accessToken.value = response.accessToken
          return response.accessToken
        } catch (interactiveError) {
          console.error('Interactive token refresh failed:', interactiveError)
          clearAuthStateAndRedirectToLogin()
          return null
        }
      } else {
        console.error('Token refresh failed with non-interaction error:', error)
        clearAuthStateAndRedirectToLogin()
        return null
      }
    }
  }

  return {
    isAuthenticated,
    isInitialized,
    user,
    accessToken,
    account: getAccount,
    initialize,
    login,
    logout,
    getAccessToken,
    refreshToken
  }
})
